import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import BannerApi from "../Api/BannerApi";
import ImageUploadApi from "../Api/ImageUploadApi";
import { toast } from "react-toastify";
import { IMAGE_BASE_URL } from "../Config/Index";
import Select from "react-select";
import { selectStyles } from "../helper/SelectStyles";
import usePermissions from "../hook/usePermissions";

const BannerFormLayer = () => {
    const { hasPermission } = usePermissions();
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const isView = location.pathname.includes("/view/");

    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        pageName: "Home",
        description: "",
        link: "",
        status: true
    });
    const [imageFile, setImageFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);
    const [existingImage, setExistingImage] = useState(null);
    const [mobileImageFile, setMobileImageFile] = useState(null);
    const [previewMobileImage, setPreviewMobileImage] = useState(null);
    const [existingMobileImage, setExistingMobileImage] = useState(null);
    const [savedImage, setSavedImage] = useState(null);
    const [errors, setErrors] = useState({});

    const statusOptions = [
        { value: true, label: "Active" },
        { value: false, label: "Inactive" },
    ];

    useEffect(() => {
        if (id) fetchDetails();
    }, [id]);

    const fetchDetails = async () => {
        setLoading(true);
        const response = await BannerApi.getAllBanners(0, 1000);
        if (response.status) {
            const banner = response.response.data.find(b => b._id === id);
            if (banner) {
                setFormData({
                    title: banner.title || "",
                    pageName: "Home",
                    description: banner.description || "",
                    link: banner.link || "",
                    status: banner.status ?? true
                });
                if (banner.image) {
                    setExistingImage(banner.image);
                    const imagePath = banner.image?.path || banner.image;
                    setPreviewImage(`${IMAGE_BASE_URL}/${imagePath}`);
                }
                if (banner.mobileImage) {
                    setExistingMobileImage(banner.mobileImage);
                    const mobileImagePath = banner.mobileImage?.path || banner.mobileImage;
                    setPreviewMobileImage(`${IMAGE_BASE_URL}/${mobileImagePath}`);
                }
            }
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleSelectChange = (selectedOption, { name }) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption ? selectedOption.value : (name === "status" ? true : "")
        }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error("Image size must be less than 2MB");
                setErrors(prev => ({ ...prev, image: "Image size must be less than 2MB" }));
                setImageFile(null);
                setPreviewImage(null);
                e.target.value = ""; // Clear file input
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPreviewImage(reader.result);
            reader.readAsDataURL(file);
            setErrors(prev => ({ ...prev, image: "" }));
        }
    };

    const handleMobileImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                toast.error("Image size must be less than 2MB");
                setErrors(prev => ({ ...prev, mobileImage: "Image size must be less than 2MB" }));
                setMobileImageFile(null);
                setPreviewMobileImage(null);
                e.target.value = ""; // Clear file input
                return;
            }
            setMobileImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setPreviewMobileImage(reader.result);
            reader.readAsDataURL(file);
            setErrors(prev => ({ ...prev, mobileImage: "" }));
        }
    };

    const handleRemoveImage = () => {
        setImageFile(null);
        if (existingImage) {
            const path = existingImage?.path || existingImage;
            setPreviewImage(`${IMAGE_BASE_URL}/${path}`);
        } else {
            setPreviewImage(null);
        }
        if (!imageFile && existingImage) {
            setExistingImage(null);
            setPreviewImage(null);
        }
    };

    const handleRemoveMobileImage = () => {
        setMobileImageFile(null);
        if (existingMobileImage) {
            const path = existingMobileImage?.path || existingMobileImage;
            setPreviewMobileImage(`${IMAGE_BASE_URL}/${path}`);
        } else {
            setPreviewMobileImage(null);
        }
        if (!mobileImageFile && existingMobileImage) {
            setExistingMobileImage(null);
            setPreviewMobileImage(null);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.title?.trim()) newErrors.title = "Banner Title is required";
        if (!formData.pageName) newErrors.pageName = "Page Name is required";
        if (formData.status === null || formData.status === undefined) newErrors.status = "Status is required";
        if (!imageFile && !previewImage) newErrors.image = "Media (Image or Video) is required";
        if (!mobileImageFile && !previewMobileImage) newErrors.mobileImage = "Mobile Banner Image is required";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setLoading(true);
        try {
            let finalImage = existingImage;
            if (imageFile) {
                const form = new FormData();
                form.append("file", imageFile);
                const uploadRes = await ImageUploadApi.uploadImage({ formData: form, path: "banners" });
                if (uploadRes.status) {
                    finalImage = uploadRes.response.data || uploadRes.response;
                } else {
                    setLoading(false);
                    return;
                }
            }

            let finalMobileImage = existingMobileImage;
            if (mobileImageFile) {
                const mobileForm = new FormData();
                mobileForm.append("file", mobileImageFile);
                const uploadRes = await ImageUploadApi.uploadImage({ formData: mobileForm, path: "banners" });
                if (uploadRes.status) {
                    finalMobileImage = uploadRes.response.data || uploadRes.response;
                } else {
                    setLoading(false);
                    return;
                }
            }

            const payload = {
                ...formData,
                image: finalImage,
                mobileImage: finalMobileImage
            };

            let res;
            if (id) {
                res = await BannerApi.updateBanner(id, payload);
            } else {
                res = await BannerApi.createBanner(payload);
            }

            if (res.status) {
                // toast.success(id ? "Banner updated successfully" : "Banner added successfully");
                navigate("/banner");
            }
        } catch (error) {
            console.error("Error submitting banner:", error);
            toast.error("Something went wrong");
        } finally {
            setLoading(false);
        }
    };

    const isVideo = (url) => {
        if (!url) return false;
        const videoExtensions = ['.mp4', '.webm', '.ogg', 'video'];
        return videoExtensions.some(ext => url.toLowerCase().includes(ext)) || url.startsWith('data:video/');
    };

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24">
                <h6 className="text-primary-600 pb-2 mb-0">{isView ? "View" : id ? "Edit" : "Add"} Banner</h6>
            </div>
            <div className="card-body p-24">
                <form onSubmit={handleSubmit}>
                    <div className="row gy-4">
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Banner Title <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className={`form-control radius-8 ${errors.title ? 'is-invalid' : ''}`}
                                name="title"
                                placeholder="Enter title"
                                value={formData.title}
                                onChange={handleChange}
                                disabled={isView}
                            />
                            {errors.title && <small className="text-danger">{errors.title}</small>}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Page Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="pageName"
                                value="Home"
                                readOnly
                                disabled
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Link (Optional)</label>
                            <input
                                type="text"
                                className="form-control radius-8"
                                name="link"
                                placeholder="Enter link URL"
                                value={formData.link}
                                onChange={handleChange}
                                disabled={isView}
                            />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Status <span className="text-danger">*</span></label>
                            <Select
                                name="status"
                                options={statusOptions}
                                value={statusOptions.find((opt) => opt.value === formData.status)}
                                onChange={handleSelectChange}
                                styles={selectStyles(errors.status)}
                                isClearable={false}
                                isDisabled={isView}
                            />
                        </div>
                        <div className="col-12">
                            <label className="form-label fw-semibold">Description</label>
                            <textarea
                                className="form-control radius-8"
                                name="description"
                                rows="3"
                                placeholder="Enter description"
                                value={formData.description}
                                onChange={handleChange}
                                disabled={isView}
                            ></textarea>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Desktop Banner Image / Video <span className="text-danger">*</span> <small className="text-secondary-light fw-normal">(Max 2MB)</small></label>
                            {!previewImage && !isView && (
                                <div className="position-relative">
                                    <input type="file" className="form-control radius-8" accept="image/*,video/*" onChange={handleImageChange} />
                                    {errors.image && <small className="text-danger">{errors.image}</small>}
                                </div>
                            )}
                            {previewImage && (
                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light-600">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-100-px h-100-px rounded-8 overflow-hidden border">
                                            {isVideo(previewImage) ? (
                                                <video src={previewImage} className="w-100 h-100 object-fit-cover" />
                                            ) : (
                                                <img src={previewImage} alt="Preview" className="w-100 h-100 object-fit-cover" />
                                            )}
                                        </div>
                                    </div>
                                    {!isView && (
                                        <button type="button" className="btn btn-icon btn-primary-100 text-danger-600 rounded-circle" onClick={handleRemoveImage} title="Delete">
                                            <Icon icon="mingcute:delete-2-line" width="24" height="24" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Mobile Banner Image <span className="text-danger">*</span> <small className="text-secondary-light fw-normal">(Max 2MB)</small></label>
                            {!previewMobileImage && !isView && (
                                <div className="position-relative">
                                    <input type="file" className="form-control radius-8" accept="image/*" onChange={handleMobileImageChange} />
                                    {errors.mobileImage && <small className="text-danger">{errors.mobileImage}</small>}
                                </div>
                            )}
                            {previewMobileImage && (
                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light-600">
                                    <div className="d-flex align-items-center gap-3">
                                        <div className="w-100-px h-100-px rounded-8 overflow-hidden border">
                                            <img src={previewMobileImage} alt="Preview Mobile" className="w-100 h-100 object-fit-cover" />
                                        </div>
                                    </div>
                                    {!isView && (
                                        <button type="button" className="btn btn-icon btn-primary-100 text-danger-600 rounded-circle" onClick={handleRemoveMobileImage} title="Delete">
                                            <Icon icon="mingcute:delete-2-line" width="24" height="24" />
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-24">
                        <Link to="/banner" className="btn btn-outline-danger-600 px-32 radius-8 justify-content-center" style={{ width: "120px" }}>Cancel</Link>
                        {!isView && (
                            <button type="submit" className="btn btn-primary radius-8 px-18 py-11 justify-content-center" disabled={loading} style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", width: "120px" }}>
                                {loading ? "Saving..." : id ? "Update" : "Save"}
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BannerFormLayer;

